import { PropsWithChildren } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { JiraLogo } from '@atlaskit/logo';

import { BlueWaveTemplate } from '@/components/signup/layouts/blue-wave-template';
import { SitePicker } from '@/components/signup/site-activation/components/common/site-picker';
import { ExistingUser } from '@/components/signup/site-activation/components/existing-user/existing-user';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { getFirstName } from '@/components/signup/utils/get-first-name';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { Picture } from '@/components/wpl/image/src/image';

import { InputEmail } from '../../../components/input-email';
import { NoCreditCardRequired } from '../../../components/no-credit-card-required';
import { SubmitButtonExistingUser } from '../../../components/submit-button-existing-user';
import { SquareVisaCiscoPfizer } from '../../components/square-visa-cisco-pfizer';

export const LoggedInJiraBlueWaveTemplateExistingUser = ({
	productEditionText,
}: PropsWithChildren<{ productEditionText: string }>) => {
	const { formatMessage } = useIntl();
	const {
		user: { name, email },
	} = useUserContext();

	return (
		<BlueWaveTemplate
			productEditionText={productEditionText}
			socialProofLogos={<SquareVisaCiscoPfizer />}
			tagline={formatMessage({
				id: 'WacSignupForm.signupOfferingIncludesFeaturesSocialProofTextJira',
				defaultMessage: 'Trusted by over 65,000 teams worldwide',
			})}
			valueProps={formatMessage({
				id: 'WacSignupForm.signupOfferingIncludesFeaturesFreeJSWFI',
				defaultMessage:
					'Scale agile practices{newline}Consolidate workflows{newline}Expand visibility{newline}Plan, track, and release',
			}).split('{newline}')}
			productLogos={[<JiraLogo appearance="inverse" size="xlarge" key="jsm" />]}
			illustration={
				<Picture
					image={{
						src: 'https://atlassian.com/dam/jcr:775fdafc-0622-4a54-acdf-1c3ca0da5e83/jira-family-hero.png',
						alt: '',
					}}
				/>
			}
		>
			<div css={containerCss}>
				<h2 css={siteActivationHeadingCss}>
					{name
						? formatMessage(
								{
									id: 'BxpUiSignup.nameSite.header',
									defaultMessage: 'Welcome back, {name}',
								},
								{ name: getFirstName(name) },
							)
						: formatMessage({
								id: 'JiraSoftwarePageTemplate.loggedInHero.welcomeBack',
								defaultMessage: 'Welcome back',
							})}
				</h2>
				<InputEmail email={email} />
				<SiteActivation
					ExistingUser={
						<ExistingUser
							SubmitButton={<SubmitButtonExistingUser />}
							SitePicker={
								<div css={sitePickerCss}>
									<SitePicker />
								</div>
							}
						/>
					}
					ManagedUser={null}
					WeekOneUser={null}
				/>
				<div css={spacingSyles}>
					<NoCreditCardRequired />
				</div>
			</div>
		</BlueWaveTemplate>
	);
};

const containerCss = css({
	display: 'grid',
	gap: '8px',
});

const siteActivationHeadingCss = css({
	fontSize: 24,
	fontWeight: 500,
	margin: 0,
	textAlign: 'center',
});

const spacingSyles = css({
	marginBlockStart: '12px',
	marginBlockEnd: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p']: {
		margin: '12px 0 0',
	},
});

const sitePickerCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['label + div > div:first-of-type']: {
		borderRadius: '3px !important',
		// eslint-disable-next-line local/ensure-wac-token-usage
		border: '2px solid rgb(179, 186, 197) !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input[disabled]']: {
		paddingInlineStart: '8px !important',
	},
});
